import {
    addDoc,
    collection,
    doc,
    query,
    getDoc,
    getDocs,
    connectFirestoreEmulator,
    getFirestore,
    limit,
    orderBy,
    onSnapshot,
    setDoc,
    updateDoc,
    where,
    writeBatch,
    startAfter,
    getCountFromServer,
    endBefore,
    limitToLast,
    deleteDoc
} from 'firebase/firestore';
import {Query} from "firebase/firestore";
import {FirebaseApp} from '@/plugins/Firebase';
import {Store} from "@/plugins/Store";
import {Article, ArticleContentKey, ArticlesPage} from "@/model/Article";
import {normalizeArticleInvisibleText} from '@/service/articleTextFields';
import {ArticleCreditUsed, readCreditUsed} from '@/service/articleCancelRefund';
import {
    COMPLETION_PARTS,
    CompletionMap,
    SaveIntegrityError,
    generationGuardsSave,
    shouldWriteArticle,
    staleRunSaveOverwritesDone,
} from '@/service/saveIntegrity';
import {GenerationSaveBlocked, generationInProgress, savedRunPastLimit} from '@/service/generationSaveGuard';
import {generationStatusWrite} from '@/service/generationStatusWrite';
import {currentRunCompletions} from '@/service/completionRun';
import {Plan} from "@/model/User";
import {planFromSubscription} from "@/service/subscriptionPlan";
import {DocumentData, Unsubscribe} from "firebase/firestore";
import {
    OpenAICompletionData,
    OpenAiCompletionType,
    OpenAiExtendPartData,
    OpenAiExtendPartDataWithId,
    OpenAiPolishData,
    OpenAITupleValue
} from "@/model/OpenAI";
import {KeywordSuggestionLite} from "@/model/DataForSeo";
import {Functions} from "@/plugins/Functions";
import {StripePrice, StripeProduct} from "@/model/Stripe";
import {Sort, SortString} from "@/model/Sort";
import {FreeAnalysesConfig, isAnalyzeArticle, startOfUtcMonth} from "@/config/FreeAnalyses";
import {ciEmulatorPorts} from "@/plugins/ciEmulators";

const db = getFirestore(FirebaseApp);
const ciEmulators = ciEmulatorPorts();
if (ciEmulators) {
    connectFirestoreEmulator(db, ciEmulators.firestoreHost, ciEmulators.firestorePort);
}

function getUserDoc(uid = Store.getters.user.uid) {
    return doc(collection(db, 'users'), uid);
}

const plansRef = collection(db, 'plans');

const articlesCollection = 'articles';

let stopObserver: Unsubscribe | null = null;
let stopConfigObserver: Unsubscribe | null = null;

function transformArticles(articles: DocumentData[]): Article[] {
    return articles.map(queriedArticle => {
        const article = Article.fromObject(queriedArticle.data());
        if (queriedArticle.data().id === null) {
            // article saved without id
            article.id = queriedArticle.id;
        }
        article.loadedSeoScore = article.seoScore;
        /*
        if (queriedArticle.data().mainKeyword === undefined) {
            // loaded article without mainKeyword
            article.mainKeyword = article.selectedKeyword === '' ? article.keyword.toLowerCase() : article.selectedKeyword.toLowerCase();
        }
        */
        return article;
    });
}

function transformArticlesPage(articles: DocumentData[]): ArticlesPage {
    return {
        first: articles[0],
        last: articles[articles.length - 1],
        items: transformArticles(articles)
    };
}

const defaultSort: Sort = {
    field: 'created_at',
    sortString: SortString.DESC
};

const Firestore = {

    // User

    getUserDataByUid: async function (uid: string): Promise<DocumentData | null> {
        const doc = await getDoc(getUserDoc(uid));
        if (doc.exists()) {
            return doc.data();
        } else {
            return null;
        }
    },

    // The Stripe extension used to create users/{uid} at signup (#30625).
    // Every later write to it is an update, which fails on a missing document.
    // Creating it starts UserOnCreateObserver, which adds the Stripe customer.
    ensureUserDoc: async function (uid: string, email: string): Promise<void> {
        const userDoc = getUserDoc(uid);
        if (!(await getDoc(userDoc)).exists()) {
            await setDoc(userDoc, {email}, {merge: true});
        }
    },

    observeUserDoc: function (handler: (value: {
        freeAnalysesLeft: number | undefined;
        theme: boolean;
        articlesCreatedThisPeriod: number;
        extraInspirations: number;
    }) => void): void {
        stopObserver = onSnapshot(getUserDoc(), snapshot => {
            const rawLeft = snapshot.get('freeAnalysesLeft');
            handler({
                freeAnalysesLeft: typeof rawLeft === 'number' ? rawLeft : undefined,
                theme: snapshot.get('theme') || false,
                articlesCreatedThisPeriod: snapshot.get('articlesCreatedThisPeriod') || 0,
                extraInspirations: snapshot.get('extraInspirations') || 0
            });
        }, () => {
            handler({
                freeAnalysesLeft: undefined,
                theme: false,
                articlesCreatedThisPeriod: 0,
                extraInspirations: 0
            });
        });
    },

    // One count() on every user-document snapshot instead of downloading up to
    // 1000 articles. Needs the isAnalyze + created_at index in
    // firestore.indexes.json; until it exists the query fails and
    // reconcileFreeAnalyses shows the stored balance.
    countAnalyzeArticlesThisMonth: async function (): Promise<number> {
        const snapshot = await getCountFromServer(query(
            collection(getUserDoc(), articlesCollection),
            where('isAnalyze', '==', true),
            where('created_at', '>=', startOfUtcMonth()),
        ));
        return snapshot.data().count;
    },

    stopObservingUser: function (): void {
        if (stopObserver !== null) {
            stopObserver();
        }
    },

    observeFreeAnalysesConfig: function (handler: (count: number) => void): void {
        const configRef = doc(db, 'config', 'FreeAnalyses');
        stopConfigObserver = onSnapshot(configRef, snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                const count = data?.count;

                if (count !== undefined && count !== null) {
                    const parsedCount = typeof count === 'number' ? count : parseInt(count, 10);
                    if (!isNaN(parsedCount) && parsedCount > 0) {
                        handler(parsedCount);
                        return;
                    }
                }
            }
            handler(FreeAnalysesConfig.defaultCount);
        }, () => {
            handler(FreeAnalysesConfig.defaultCount);
        });
    },

    stopObservingFreeAnalysesConfig: function (): void {
        if (stopConfigObserver !== null) {
            stopConfigObserver();
            stopConfigObserver = null;
        }
    },

    setTheme: function (value: boolean): Promise<void> {
        return updateDoc(getUserDoc(), {theme: value});
    },

    // Products

    getAllActiveProducts: function (): Promise<Array<StripeProduct>> {
        return getDocs(query(plansRef, where('active', '==', true)))
            .then(data => {
                return data.docs.map(doc => (
                    {
                        ...doc.data(),
                        id: doc.id
                    } as StripeProduct
                ));
            });
    },

    getActivePricesForProduct: function (planId: string): Promise<Array<StripePrice>> {
        return getDocs(query(collection(doc(plansRef, planId), 'prices'), where('active', '==', true)))
            .then(data => {
                return data.docs.map(doc => (
                    {
                        ...doc.data(),
                        id: doc.id
                    } as StripePrice
                ));
            });
    },

    // Plans

    getCurrentPlan: function (): Promise<Plan | null> {
        return getDocs(query(collection(getUserDoc(), 'subscriptions'),
            where('status', 'in', ['active', 'trialing']))
        ).then(data => {
            if (data.docs.length < 1) {
                return null;
            } else {
                const plan = data.docs[0].data();
                if (plan) {
                    return planFromSubscription(plan);
                } else {
                    return null;
                }
            }
        });
    },

    // Keywords

    CACHE_LIFETIME: 1000 * 60 * 60 * 24 * 90, // 90 day
    getKeywordSuggestions: function (location_code: string, keyword: string): Promise<KeywordSuggestionLite[]> {
        return new Promise((resolve) => {
            getDoc(doc(collection(db, 'd4s_cache_suggestions_lite'), `${keyword}-${location_code}`))
                .then(document => {
                    if (document.exists()) {
                        if ((document.data().timestamp + this.CACHE_LIFETIME) > Date.now()) {
                            resolve(document.data().value as KeywordSuggestionLite[]);
                        } else {
                            throw new Error("Item in cache is too old");
                        }
                    } else {
                        throw new Error("Item is not in cache");
                    }
                }).catch(() => {
                Functions.D4SGetKeywordSuggestions({
                    location_code: location_code,
                    language_code: '', // not required
                    keyword: keyword
                }).then(response => {
                    resolve(response || []);
                }).catch(() => {
                    resolve([]);
                });
            });
        });
    },

    // Articles

    checkIfSeoScoreHistoryCollectionExists: function (article: Article): Promise<boolean> {
        return getDocs(
            query(
                collection(doc(collection(getUserDoc(), articlesCollection), article.id as string), 'seoScoreHistory'),
                limit(1)
            )
        )
            .then(history => {
                return history.docs.length > 0;
            });
    },

    /** Completion rows of the article's newest run (see completionRun.ts). */
    loadArticleCompletions: async function (articleId: string): Promise<CompletionMap> {
        const pairs = await Promise.all(COMPLETION_PARTS.map(async (part) => {
            const values = await this.getArticleOpenAIValues(articleId, part);
            return [part, values] as const;
        }));
        return currentRunCompletions(Object.fromEntries(pairs)) as CompletionMap;
    },

    /**
     * integrityChecked: the caller (commitCoveringArticle) already ran the
     * completion check for this save, so it is not repeated here.
     */
    saveUserArticle: async function (
        article: Article,
        options?: {generationStart?: boolean; integrityChecked?: boolean},
    ): Promise<string> {
        normalizeArticleInvisibleText(article);
        if (!options?.generationStart && generationInProgress(article)) {
            return Promise.reject(new GenerationSaveBlocked());
        }
        if (article.id && !options?.integrityChecked) {
            // Completions are read only while generation still owns the stored article.
            const stored = await this.getUserArticle(article.id);
            if (!options?.generationStart && staleRunSaveOverwritesDone(article, stored)) {
                return Promise.reject(new SaveIntegrityError());
            }
            if (generationGuardsSave(stored)) {
                const completions = await this.loadArticleCompletions(article.id);
                if (!shouldWriteArticle(article, stored, completions)) {
                    return Promise.reject(new SaveIntegrityError());
                }
            }
        }
        Store.commit('setArticleSaving', true);
        if (article.id) {
            // update existing article (batch write)
            const batch = writeBatch(db);
            if (article.loadedSeoScore !== article.seoScore || (article.forcedEmpty && article.loadedSeoScore === 0 && article.seoScore === 0)) {
                // if SEO score changed, create new document in seoScoreHistory collection
                const historyRef = doc(collection(doc(collection(getUserDoc(), articlesCollection), article.id as string), 'seoScoreHistory'));
                batch.set(historyRef, {
                    score: article.seoScore,
                    created_at: Date.now()
                });
            }
            const payload = article.toObject();
            if (!options?.generationStart && savedRunPastLimit(article)) {
                // The watchdog may have written failed; running from memory would undo it.
                delete payload.enhanced;
                delete payload.aiGenerateStatus;
            }
            batch.update(doc(collection(getUserDoc(), articlesCollection), article.id), payload as any);
            return batch.commit()
                .then(() => {
                    article.loadedSeoScore = article.seoScore;
                    return article.id as string;
                })
                .catch(() => {
                    return Promise.reject();
                })
                .finally(() => {
                    Store.commit('setArticleSaving', false);
                });
        } else {
            // create new article
            article.created_at = Date.now();
            // article.mainKeyword = article.keyword.toLowerCase();
            return addDoc(collection(getUserDoc(), articlesCollection), article.toObject())
                .then((reference) => {
                    // save ID to article object
                    article.id = reference.id;
                    return updateDoc(doc(collection(getUserDoc(), articlesCollection), reference.id), article.toObject() as any);
                })
                .then(() => {
                    if (article.isAnalyze) {
                        Store.dispatch('reconcileFreeAnalyses', Store.getters.user?.freeAnalysesLeft);
                    }
                    return article.id as string;
                })
                .catch(() => {
                    return Promise.reject();
                })
                .finally(() => {
                    Store.commit('setArticleSaving', false);
                });
        }
    },

    updateUserArticleFields: function (article: Article, data: Partial<Article>): Promise<string> {
        const keys = Object.keys(article) as (keyof typeof article)[];
        keys.forEach(key => {
            if (data[key] !== undefined) {
                (article[key] as any) = data[key];
            }
        });
        return this.saveUserArticle(article);
    },

    updateArticleGenerationStatus: function (
        article: Article,
        data: {enhanced: Article['enhanced']; aiGenerateStatus: Article['aiGenerateStatus']},
    ): Promise<string> {
        const payload = generationStatusWrite(data);
        article.enhanced = payload.enhanced;
        article.aiGenerateStatus = payload.aiGenerateStatus;
        if (!article.id) {
            return Promise.resolve('');
        }
        return updateDoc(doc(collection(getUserDoc(), articlesCollection), article.id), payload).then(() => article.id as string);
    },

    /**
     * Local Preview without functions only: writes the article's share token
     * by itself. A full save no longer carries shareToken (#30940).
     */
    updateArticleShareToken: function (article: Article, token: string | null): Promise<void> {
        article.shareToken = token;
        if (!article.id) {
            return Promise.resolve();
        }
        return updateDoc(doc(collection(getUserDoc(), articlesCollection), article.id), {shareToken: token});
    },

    updateArticleAIGeneratedAt: function (article: Article): Promise<void> {
        article.ai_content_last_generated_at = Date.now();
        if (article.id) {
            return updateDoc(doc(collection(getUserDoc(), articlesCollection), article.id), {
                ai_content_last_generated_at: Date.now()
            });
        } else {
            return Promise.resolve();
        }
    },

    getUserArticle: function (articleId: string): Promise<Article | null> {
        return getDoc(doc(collection(getUserDoc(), articlesCollection), articleId))
            .then(article => {
                if (article.exists()) {
                    const loaded = Article.fromObject({
                        ...article.data(),
                        id: article.id,
                    });
                    loaded.loadedSeoScore = loaded.seoScore;
                    return loaded;
                } else {
                    return null;
                }
            });
    },

    createUserArticleWatcher: function (articleId: string, handler: (article: Article) => void): Unsubscribe {
        return onSnapshot(doc(collection(getUserDoc(), articlesCollection), articleId), snapshot => {
            if (snapshot.exists()) {
                handler(snapshot.data() as Article);
            }
        });
    },

    /** creditUsed is written by ArticleOnCreateObserver after create, so the loaded article may not have it yet. */
    createArticleCreditUsedWatcher: function (articleId: string, handler: (creditUsed: ArticleCreditUsed | null) => void): Unsubscribe {
        return onSnapshot(doc(collection(getUserDoc(), articlesCollection), articleId), snapshot => {
            handler(readCreditUsed(snapshot.exists() ? snapshot.get('creditUsed') : null));
        });
    },

    getUserArticles: function (limitAmount = 1000): Promise<Article[]> {
        return getDocs(
            query(
                collection(getUserDoc(), articlesCollection),
                orderBy(defaultSort.field, defaultSort.sortString),
                limit(limitAmount)
            )
        ).then(articles => {
            return transformArticles(articles.docs);
        });
    },

    getUserArticlesNextPage: function (itemsPerPage = 10, sort: Sort = defaultSort, article?: DocumentData): Promise<ArticlesPage> {
        let paginationQuery: Query<DocumentData> | null;
        if (!article) {
            // first page
            paginationQuery = query(
                collection(getUserDoc(), articlesCollection),
                orderBy(sort.field, sort.sortString),
                limit(itemsPerPage)
            );
        } else {
            // next page
            paginationQuery = query(
                collection(getUserDoc(), articlesCollection),
                orderBy(sort.field, sort.sortString),
                startAfter(article),
                limit(itemsPerPage),
            );
        }

        return getDocs(paginationQuery).then(articles => {
            return transformArticlesPage(articles.docs);
        });
    },

    getUserArticlesPreviousPage: function (itemsPerPage = 10, sort: Sort = defaultSort, article?: DocumentData): Promise<ArticlesPage> {
        return getDocs(
            query(
                collection(getUserDoc(), articlesCollection),
                orderBy(sort.field, sort.sortString),
                endBefore(article),
                limitToLast(itemsPerPage),
            )
        ).then(articles => {
            return transformArticlesPage(articles.docs);
        });
    },

    getUserArticlesCount: function (): Promise<number> {
        return getCountFromServer(
            query(
                collection(getUserDoc(), articlesCollection)
            )
        ).then(snapshot => {
            return snapshot.data().count;
        });
    },

    getUserArticleOverview: async function (sampleSize = 100): Promise<{
        total: number;
        sampled: number;
        generated: number;
        analyzed: number;
        analyzedThisMonth: number;
        withUrl: number;
        avgSeoScore: number | null;
        bestSeoScore: number | null;
        oldestCreatedAt: number | null;
        recent: Array<{
            id: string;
            title: string;
            seoScore: number;
            created_at: number;
            isAnalyze: boolean;
        }>;
    }> {
        const [total, articles] = await Promise.all([
            this.getUserArticlesCount(),
            this.getUserArticles(sampleSize),
        ]);
        const scored = articles.filter(a => typeof a.seoScore === 'number' && a.seoScore > 0);
        const avgSeoScore = scored.length
            ? Math.round(scored.reduce((sum, a) => sum + a.seoScore, 0) / scored.length)
            : null;
        const bestSeoScore = scored.length
            ? Math.max(...scored.map(a => a.seoScore))
            : null;
        const monthStart = startOfUtcMonth();
        const createdDates = articles
            .map(a => a.created_at)
            .filter((d): d is number => typeof d === 'number' && d > 0);
        return {
            total,
            sampled: articles.length,
            generated: articles.filter(a => !isAnalyzeArticle(a)).length,
            analyzed: articles.filter(a => isAnalyzeArticle(a)).length,
            analyzedThisMonth: articles.filter(a => isAnalyzeArticle(a) && (a.created_at || 0) >= monthStart).length,
            withUrl: articles.filter(a => !!(a.url && a.url.trim())).length,
            avgSeoScore,
            bestSeoScore,
            oldestCreatedAt: createdDates.length ? Math.min(...createdDates) : null,
            recent: articles.slice(0, 5).map(a => ({
                id: a.id as string,
                title: (a.selectedKeyword || a.keyword || '').trim() || (a.id as string),
                seoScore: a.seoScore || 0,
                created_at: a.created_at,
                isAnalyze: !!a.isAnalyze,
            })),
        };
    },

    removeUserArticle: function (articleId: string): Promise<void> {
        return deleteDoc(doc(collection(getUserDoc(), articlesCollection), articleId));
    },

    // Article OpenAI completion

    createArticleOpenAIWatcher: function (articleId: string, part: OpenAiCompletionType, handler: (content: OpenAICompletionData) => void): Unsubscribe {
        return onSnapshot(
            doc(collection(doc(collection(getUserDoc(), articlesCollection), articleId), 'OpenAICompletion'), part),
            snapshot => {
                if (snapshot.exists()) {
                    handler(snapshot.data() as OpenAICompletionData);
                }
            }
        );
    },

    getArticleOpenAIState: function (articleId: string, part: OpenAiCompletionType): Promise<OpenAICompletionData | null> {
        return getDoc(doc(collection(doc(collection(getUserDoc(), articlesCollection), articleId), 'OpenAICompletion'), part))
            .then(state => {
                if (state.exists()) {
                    return state.data() as OpenAICompletionData;
                } else {
                    return null;
                }
            })
            .catch(() => {
                return null;
            });
    },

    getArticleOpenAIValues: function (articleId: string, part: OpenAiCompletionType): Promise<OpenAITupleValue[]> {
        return getDocs(collection(doc(collection(doc(
            collection(getUserDoc(), articlesCollection), articleId
        ), 'OpenAICompletion'), part), 'values'))
            .then(completions => {
                return completions.docs
                    .map(doc => ({
                        ...doc.data()
                    } as OpenAITupleValue))
                    .sort((a, b) => {
                        const left = typeof a.seq === 'number' ? a.seq : Number.MAX_SAFE_INTEGER;
                        const right = typeof b.seq === 'number' ? b.seq : Number.MAX_SAFE_INTEGER;
                        return left - right;
                    });
            })
            .catch(() => {
                return [];
            });
    },

    checkArticlePolishGotStuck: function (articleId: string): Promise<boolean> {
        const polishDocRef = doc(collection(doc(collection(getUserDoc(), articlesCollection), articleId), 'OpenAIPolish'), 'polish');
        return getDoc(polishDocRef)
            .then(doc => {
                if (doc.exists()) {
                    const data = doc.data() as OpenAiPolishData;
                    if ((data.timestamp && Date.now() - data.timestamp > 10 * 60 * 1000) || data.error) {
                        // polish job is older than 10 minutes or error occurred
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            })
            .catch(() => {
                window.console.error("Error checking polish status");
                return false;
            });
    },

    // Article OpenAI part extensions

    _openAiExtensionCollection(articleId: string, part: ArticleContentKey, uid: number) {
        return collection(doc(collection(doc(collection(
            doc(collection(getUserDoc(), articlesCollection), articleId), 'OpenAIExtensions'
        ), part), 'uids'), uid.toString(10)), 'values');
    },

    getArticleOpenAiPartExtensions: function (articleId: string, part: ArticleContentKey, uid: number)
        : Promise<OpenAiExtendPartDataWithId[]> {
        return getDocs(this._openAiExtensionCollection(articleId, part, uid))
            .then(extensionValues => {
                return extensionValues.docs.map(doc => ({
                    valueId: doc.id,
                    ...doc.data()
                } as OpenAiExtendPartDataWithId));
            }).catch(error => {
                window.console.error(error);
                return [];
            });
    },

    setArticleOpenAiPartExtensionUsed: function (
        articleId: string, part: ArticleContentKey, uid: number, valueId: string, used: boolean
    ): Promise<void> {
        return updateDoc(doc(this._openAiExtensionCollection(articleId, part, uid), valueId), {
            used: used
        });
    },

    setArticleOpenAiPartExtensionDiscarded: function (
        articleId: string, part: ArticleContentKey, uid: number, valueId: string, discarded: boolean
    ): Promise<void> {
        return updateDoc(doc(this._openAiExtensionCollection(articleId, part, uid), valueId), {
            discarded: discarded
        });
    },

    createArticleOpenAiPartExtensionWatcher: function (
        articleId: string, part: ArticleContentKey, uid: number, valueId: string,
        handler: (content: OpenAiExtendPartData) => void): Unsubscribe {
        return onSnapshot(doc(this._openAiExtensionCollection(articleId, part, uid), valueId),
            snapshot => {
                if (snapshot.exists()) {
                    handler(snapshot.data() as OpenAiExtendPartData);
                }
            }
        );
    },

};

export {Firestore};
